import React, { useState } from 'react';
import styles from '../styles/joinPreferencesModal.module.css';

export interface JoinPreferences {
  display_preference: string;
  include_on_printed_phone_list: boolean;
  email_notifications_enabled: boolean;
  sponsor_willingness: string;
}

const DEFAULT_PREFERENCES: JoinPreferences = {
  display_preference: 'name_only',
  include_on_printed_phone_list: false,
  email_notifications_enabled: true,
  sponsor_willingness: 'no',
};

const DISPLAY_OPTIONS = [
  { value: 'name_only', label: 'Name only (first name + last initial)' },
  { value: 'name_and_location', label: 'Name and location' },
  { value: 'name_and_phone', label: 'Name and phone' },
  { value: 'hidden', label: 'Hidden (don’t show me on the list)' },
];

const SPONSOR_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
  { value: 'temporary', label: 'Temporarily' },
];

interface JoinPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (preferences: JoinPreferences) => void;
  isSubmitting?: boolean;
  meetingName?: string;
}

const JoinPreferencesModal: React.FC<JoinPreferencesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  meetingName,
}) => {
  const [display_preference, setDisplayPreference] = useState(DEFAULT_PREFERENCES.display_preference);
  const [include_on_printed_phone_list, setIncludeOnPrintedPhoneList] = useState(DEFAULT_PREFERENCES.include_on_printed_phone_list);
  const [email_notifications_enabled, setEmailNotificationsEnabled] = useState(DEFAULT_PREFERENCES.email_notifications_enabled);
  const [sponsor_willingness, setSponsorWillingness] = useState(DEFAULT_PREFERENCES.sponsor_willingness);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      display_preference,
      include_on_printed_phone_list,
      email_notifications_enabled,
      sponsor_willingness,
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Add you to this meeting</h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.body}>
            {meetingName && (
              <p className={styles.meetingName}>{meetingName}</p>
            )}
            <label className={styles.label}>
              How I appear on the member list
            </label>
            <select
              value={display_preference}
              onChange={(e) => setDisplayPreference(e.target.value)}
              className={styles.select}
            >
              {DISPLAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={include_on_printed_phone_list}
                onChange={(e) => setIncludeOnPrintedPhoneList(e.target.checked)}
              />
              <span>Include my phone on the printed phone list (when I share phone)</span>
            </label>

            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={email_notifications_enabled}
                onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
              />
              <span>Send me meeting emails (announcements and reminders)</span>
            </label>

            <label className={styles.label}>
              Willing to sponsor in this meeting?
            </label>
            <select
              value={sponsor_willingness}
              onChange={(e) => setSponsorWillingness(e.target.value)}
              className={styles.select}
            >
              {SPONSOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <p className={styles.privacyNote}>
              Only other members of this meeting can see your choices.
            </p>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.primaryBtn} disabled={isSubmitting}>
              {isSubmitting ? 'Adding…' : 'Add me to this meeting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JoinPreferencesModal;
export { DEFAULT_PREFERENCES };
